import cn from 'classnames';
import React, {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { StringSchema } from 'yup';

import { TOOLTIP } from '../../pages/constants';
import { KEY } from '../../utilities/events';
import { isWidthTruncated } from '../../utilities/events/validate';
import Control from '../form/Control';

type BlurAction = 'submit' | 'reset';

export const resolveBlurAction = (
  cellValue: string,
  value: string,
  isCellValueValid: boolean
): BlurAction =>
  cellValue.trim() !== value && isCellValueValid ? 'submit' : 'reset';

type CellProps = Omit<
  ComponentPropsWithoutRef<'input'>,
  'id' | 'onSubmit' | 'value'
> & {
  children?: ReactNode;
  id?: number;
  initialValidateEnabled?: boolean;
  isFailed?: boolean;
  isOutsideClickEnabled?: boolean;
  isSuccessful?: boolean;
  onSubmit?: (value: string) => void;
  validationSchema?: StringSchema;
  value?: string;
  maxLength?: number;
  colSpan?: number;
};

const Cell = (props: CellProps, ref: React.ForwardedRef<HTMLElement>) => {
  const {
    className,
    children,
    id,
    initialValidateEnabled = false,
    isFailed,
    isOutsideClickEnabled = true,
    isSuccessful,
    onBlur,
    onSubmit,
    validationSchema,
    value = '',
    maxLength,
    colSpan,
    ...rest
  } = props;
  const [cellValue, setCellValue] = useState(value);
  const [isCellValueValid, setIsCellValueValid] = useState(true);
  const [isValidateEnabled, setIsValidateEnabled] = useState(
    initialValidateEnabled
  );
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [isCellTruncated, setIsCellTruncated] = useState(false);
  const [invalidMsg, setInvalidMsg] = useState('');

  // Set by Enter/Esc so their own blur() doesn't re-run submit/discard logic.
  const skipBlurRef = useRef(false);

  useEffect(() => setCellValue(value), [value]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setCellValue(e.target.value);

  const handleKeyPress = (evt: KeyboardEvent<HTMLInputElement>) => {
    switch (evt.key) {
      case KEY.ENTER:
        {
          if (isCellValueValid) {
            if (cellValue.trim() !== value) {
              onSubmit?.(cellValue.trim());
            }
            skipBlurRef.current = true;
            evt.currentTarget.blur();
          }
        }
        break;
      case KEY.ESC:
        {
          setCellValue(value);
          skipBlurRef.current = true;
          evt.currentTarget.blur();
        }
        break;
    }
  };

  const handleBlur = (evt: FocusEvent<HTMLInputElement>) => {
    if (isOutsideClickEnabled && !skipBlurRef.current) {
      if (resolveBlurAction(cellValue, value, isCellValueValid) === 'submit') {
        onSubmit?.(cellValue.trim());
      } else {
        setCellValue(value);
      }
    }
    skipBlurRef.current = false;
    onBlur?.(evt);
  };

  const handleMouseEnter = (evt: React.MouseEvent<HTMLElement>) => {
    if (isWidthTruncated(evt)) {
      setIsCellTruncated(true);
    }
  };

  const handleToggle = (isVisible: boolean) =>
    isCellTruncated && setIsTooltipVisible(isVisible);

  useEffect(() => {
    if (isValidateEnabled) {
      try {
        validationSchema?.trim().validateSync(cellValue);
        setIsCellValueValid(true);
        setInvalidMsg('');
      } catch (error) {
        setIsCellValueValid(false);
        setInvalidMsg(error instanceof Error ? error.message : '');
      }
    } else {
      setIsValidateEnabled(true);
    }
  }, [cellValue]);
  return (
    <td
      className={cn('Cell', className)}
      ref={ref as React.Ref<HTMLTableCellElement>}
      {...(colSpan && { colSpan })}
    >
      <div className='cellContainer'>
        <OverlayTrigger
          show={isTooltipVisible}
          onToggle={handleToggle}
          placement='top'
          delay={{ show: TOOLTIP.SHOW_DELAY, hide: TOOLTIP.HIDE_DELAY }}
          overlay={<Tooltip id={`cell-${id}`}>{value}</Tooltip>}
        >
          {/* @ts-expect-error - Control is not typed */}
          <Control
            maxLength={maxLength ? maxLength + 1 : undefined}
            onMouseEnter={handleMouseEnter}
            isFailed={isFailed || !isCellValueValid}
            isSuccessful={isSuccessful}
            onKeyDown={handleKeyPress}
            onBlur={handleBlur}
            onChange={onChange}
            value={cellValue}
            validationError={invalidMsg}
            {...rest}
          />
        </OverlayTrigger>
        {children}
      </div>
    </td>
  );
};

export default forwardRef(Cell);
